/**
 * RUM allowlist — the single source of truth for what the portal is permitted
 * to send to CloudWatch RUM. Enforced by `telemetrySanitizer.ts`. This file is
 * the code form of the allowlist signed off in
 * `docs/observability/telemetry-inventory.md` (§C). Nothing else in the portal
 * should hard-code event names or attribute keys — import from here.
 *
 * Privacy contract: PATIENT data is never allowed — no patient identifier,
 * free-form clinical text, URL, or raw error string is ever an allowed key OR
 * value (`FORBIDDEN_KEY_PATTERNS` still blocks everything non-allowlisted).
 * As of 2026-07-22 two low-identifying keys are added so an error names WHICH
 * USER and CLINIC without a second lookup: `appUserId` (the OPAQUE Cognito
 * id — meaningless to an outsider, resolvable only internally) and `clinicId`.
 * With the pre-existing `clinicianRole` (clinician/patient), an error shows
 * user + clinic + role for ANY logged-in user. NO plaintext PII: email and full
 * name are deliberately NOT allowed. The two ids bypass id-redaction via
 * `IDENTITY_KEYS` in `telemetrySanitizer.ts`; patient clinical data stays denied.
 */

/** C2 — the only event names `trackPortalEvent` will emit. */
export const PORTAL_EVENTS = {
  AUTH_LOGIN_SUCCEEDED: 'auth.login_succeeded',
  AUTH_LOGIN_FAILED: 'auth.login_failed',
  // Self-service change-password (Settings > My Profile). The payload carries
  // only `outcome`, `featureArea` and the classifier's fixed
  // `normalizedErrorCode` — never the password, its length, or its rule vector.
  AUTH_PASSWORD_CHANGE_SUCCEEDED: 'auth.password_change_succeeded',
  AUTH_PASSWORD_CHANGE_FAILED: 'auth.password_change_failed',
  // A forgot-password request that produced NO email, for a reason the UI
  // deliberately hides from the user (no such account, never-activated invite,
  // disabled account). Suppressing it on screen is required — otherwise the form
  // becomes an email-enumeration oracle — but that also made the failure invisible
  // everywhere, so it took a person complaining to discover it. The payload carries
  // only the fixed `normalizedErrorCode` from `classifyForgotPasswordError`; the
  // address is NEVER included.
  AUTH_PASSWORD_RESET_UNAVAILABLE: 'auth.password_reset_unavailable',
  PORTAL_ROUTE_LOAD_FAILED: 'portal.route_load_failed',
  PORTAL_ERROR: 'portal.error',
  PORTAL_API_FAILED: 'portal.api_failed',
  TREATMENT_PLAN_SAVE_REQUESTED: 'treatment_plan.save_requested',
  TREATMENT_PLAN_SAVE_SUCCEEDED: 'treatment_plan.save_succeeded',
  TREATMENT_PLAN_SAVE_FAILED: 'treatment_plan.save_failed',
  NOTE_GENERATE_REQUESTED: 'note.generate_requested',
  NOTE_GENERATE_SUCCEEDED: 'note.generate_succeeded',
  NOTE_GENERATE_FAILED: 'note.generate_failed',
  NOTE_SAVE_SUCCEEDED: 'note.save_succeeded',
  NOTE_SIGN_SUCCEEDED: 'note.sign_succeeded',
  EMR_HANDSHAKE_REQUESTED: 'emr.handshake_requested',
  EMR_HANDSHAKE_SUCCEEDED: 'emr.handshake_succeeded',
  EMR_HANDSHAKE_FAILED: 'emr.handshake_failed',
  EMR_EXTENSION_UNREACHABLE: 'emr.extension_unreachable',
  EMR_ACTION_STARTED: 'emr.action_started',
  EMR_ACTION_SUCCEEDED: 'emr.action_succeeded',
  EMR_ACTION_FAILED: 'emr.action_failed',
  SESSION_RESULTS_LOAD_FAILED: 'session_results.load_failed',
  SUPPORT_MODE_STARTED: 'support_mode.started',
  PORTAL_WORKFLOW_TIMING: 'portal.workflow_timing',
  // Perci drafts (intent routing): a plan or note Perci proposed and what the
  // clinician did with it. Payload is ONLY `perciDraftEventPayload` below — the
  // draft kind and outcome, never its content, the patient, or the words typed.
  PERCI_DRAFT_CREATED: 'perci.draft_created',
  PERCI_DRAFT_HANDED_OFF: 'perci.draft_handed_off',
  PERCI_DRAFT_OPENED: 'perci.draft_opened',
  PERCI_DRAFT_SAVED: 'perci.draft_saved',
  PERCI_DRAFT_SAVE_FAILED: 'perci.draft_save_failed',
  PERCI_DRAFT_DISCARDED: 'perci.draft_discarded',
  PERCI_DRAFT_ASKED: 'perci.draft_asked',
  BREADCRUMB_CLICK: 'breadcrumb.click',
  BREADCRUMB_API: 'breadcrumb.api',
} as const;

export type PortalEventName = (typeof PORTAL_EVENTS)[keyof typeof PORTAL_EVENTS];

const PORTAL_EVENT_NAME_SET: ReadonlySet<string> = new Set(Object.values(PORTAL_EVENTS));

export function isAllowedEventName(name: string): name is PortalEventName {
  return PORTAL_EVENT_NAME_SET.has(name);
}

/** C1 — the only session-attribute keys allowed (respects RUM's metadata cap). */
export const SESSION_ATTRIBUTE_KEYS = [
  'applicationVersion',
  'deploymentEnvironment',
  'awsRegion',
  'backendRegion',
  'clinicianRole',
  // User + clinic identity — deliberately allowed (2026-07-22) so an error names
  // WHICH USER and CLINIC without a backend lookup, for ANY logged-in user
  // (clinician or patient). appUserId is the OPAQUE Cognito id (no plaintext
  // PII); clinicId is the tenant id (resolved to a clinic name in the EOD report).
  // Email + full name are deliberately NOT here. Values bypass id-redaction via
  // IDENTITY_KEYS in telemetrySanitizer.ts; patient clinical data stays denied.
  'appUserId',
  'clinicId',
  'authenticationMethod',
  'releaseChannel',
  'supportMode',
  'supportCorrelationId',
] as const;

export type SessionAttributeKey = (typeof SESSION_ATTRIBUTE_KEYS)[number];

export const SESSION_ATTRIBUTE_KEY_SET: ReadonlySet<string> = new Set(SESSION_ATTRIBUTE_KEYS);

/** C3 — the only property keys allowed inside an event payload. */
export const EVENT_PROPERTY_KEYS = [
  'outcome',
  'apiOperation',
  'method',
  'httpStatusGroup',
  'normalizedErrorCode',
  'durationBucket',
  'durationMs', // exact numeric duration is allowed in the payload, never as a dimension
  'retryCount',
  'isOffline',
  'cancelled',
  'featureArea',
  'pageTemplate',
  'screenType',
  'recoverable',
  'retried',
  'fallbackShown',
  'componentName',
  'extensionVersion',
  'backendRegion',
  'supportCorrelationId',
  'action',
  'route',
  'errorFingerprint',
  'workflowName',
  'workflowStep',
  'triggerType',
  // Count of user-actionable auto-fill skips on an otherwise-successful EMR
  // action (element-not-present misses the clinician must fill by hand). 0 on
  // a clean success. Keeps RUM consistent with the portal's "couldn't
  // auto-fill" warning toast — see runActionClient.runEmrAction.
  'autofillSkipCount',
  'validationErrorCategory',
  'navigationSource',
] as const;

export type EventPropertyKey = (typeof EVENT_PROPERTY_KEYS)[number];

export const EVENT_PROPERTY_KEY_SET: ReadonlySet<string> = new Set(EVENT_PROPERTY_KEYS);

/**
 * C4 — secondary defense. Substrings that must never appear in a telemetry key
 * name. The PRIMARY mechanism is the allowlist above (anything not allowlisted
 * is dropped); this catches an attempt to add a forbidden-looking key and lets
 * tests assert intent. Allowlisted keys are exempt (e.g. `componentName` is
 * allowed even though it contains "name").
 */
export const FORBIDDEN_KEY_PATTERNS: readonly string[] = [
  'patient',
  'clinician',
  'email',
  'mrn',
  'dob',
  'phone',
  'address',
  'diagnos',
  'note',
  'token',
  'cookie',
  'password',
  'secret',
  'ssn',
  'tenant',
  'clinic',
  'firstname',
  'lastname',
  'fullname',
  'url',
  'query',
  'body',
  'payload',
  'authorization',
];

export function isForbiddenKey(key: string): boolean {
  if (EVENT_PROPERTY_KEY_SET.has(key) || SESSION_ATTRIBUTE_KEY_SET.has(key)) {
    return false;
  }
  const lower = key.toLowerCase();
  return FORBIDDEN_KEY_PATTERNS.some((pattern) => lower.includes(pattern));
}

/** A sanitized, RUM-safe payload is a flat map of primitives. */
export type SafePayload = Record<string, string | number | boolean>;

/** The draft kinds a `perci.draft_*` event may name — the action kind, nothing else. */
export type PerciDraftEventKind =
  | 'plan.new'
  | 'plan.update'
  | 'note.draft'
  | 'content.draft'
  | 'chart.draft'
  | 'none';

/**
 * The ONE payload shape for `perci.draft_*` events.
 *
 * Built here rather than at the call sites so that nobody can pass the draft
 * itself, its target plan id, or a note's text: the keys are fixed and all four
 * are already on the C3 allowlist. `triggerType` says whether the result reached
 * its destination from the chat card (`chat`) or the tab's own editor (`tab`).
 */
export function perciDraftEventPayload(
  kind: PerciDraftEventKind,
  outcome: string,
  triggerType: 'chat' | 'tab',
): SafePayload {
  return { featureArea: 'perci', action: kind, outcome, triggerType };
}
